import { createContext, FunctionComponent } from 'preact';
import { useCallback, useContext, useMemo, useState } from 'preact/hooks';
import type { Asset } from '@schibsted-svp/svp-api-types';

import { getRawAsset } from '../../../utils/player/asset';

import { usePlayer } from './PlayerProvider';
import { usePlayerEvent } from './usePlayerEvent';

export interface CurrentAssetContextProps {
  asset?: Asset;
}

const CurrentAssetContext = createContext<CurrentAssetContextProps>(undefined!);

export const CurrentAssetProvider: FunctionComponent = ({ children }) => {
  const player = usePlayer();

  const [asset, setAsset] = useState(getRawAsset(player));

  const handleAssetReady = useCallback(() => {
    setAsset(getRawAsset(player));
  }, [player]);

  usePlayerEvent('assetReady', handleAssetReady);

  const state = useMemo(() => {
    return {
      asset,
    };
  }, [asset]);

  return (
    <CurrentAssetContext.Provider value={state}>
      {children}
    </CurrentAssetContext.Provider>
  );
};

export const useCurrentAsset = () => {
  const currentAsset = useContext(CurrentAssetContext);

  if (process.env.NODE_ENV !== 'production' && !currentAsset) {
    console.warn(
      'Asset context is undefined, please verify you are calling useCurrentAsset() as child of a <CurrentAssetProvider> component.'
    );
  }

  return currentAsset;
};
